define('hadoop/listeners/featureflagged',['hadoop/hadoop-wrapper',
    'hadoop/listeners/user-event-handlers',
    'adlhelpers',
    'domhelpers',
    'edapUnderscore',
    'hadoop/pageid-helper'
], function(hadoop, userEventHandlers, ADL, domHelpers, _, pageIdHelper) {
    'use strict';

    // Module globals that are set in various functions at runtime
    var enablePageviewTest = false,
        enableGenericTest = false,
        enableNamedTest = false,
        enableLoadedTest = false,
        loadedRegex = /^[a-z]+[.](load|did[.]load)$/,
        genericRegex = /^generic[.].[a-z.]+/,
        ignoredEvents;

    /**
     * Builds the list of events that should be ignored and NEVER sent to Hadoop.
     * NOTE: 'edap.error' will always be sent to Hadoop
     *
     * @param {Array} [eventList] - Array of event names that should never be sent to Hadoop
     */
    function setupEventsToIgnore(eventList) {
        var edapErrorIndex;

        // build the list of events to ignore
        ignoredEvents = eventList;

        // Look to see if 'edap.error' is in the list
        edapErrorIndex = _.indexOf(ignoredEvents, 'edap.error');

        // Prepare the ignoredEvents list by removing 'edap.error' since we want to load that to Hadoop
        if (edapErrorIndex > -1) {
            ignoredEvents.splice(edapErrorIndex, 1);
        }
    }


    /**
     * Checks if an eventName is in the list of eventNames to be ignored and NEVER sent to Hadoop
     *
     * @param {String} [eventName] - Name of an EDAP event to check
     * @returns {Boolean} - true if this event should be ignored and not sent to Hadoop (just based on eventName)
     *                      false if it should be processed
     */
    function isEventToIgnore(eventName) {
        return _.indexOf(ignoredEvents, eventName) > -1;
    }


    /**
     * Reads the features and sets some cached module globals with their status
     *
     * @param {Features} [features] - Features instance, like from edap.public.features
     */
    function readAndSetFeatureFlags(features) {
        // Caching the state of these tests for easier readability
        enablePageviewTest = features.getFeatureProperty('edapFeatureHadoopPageviewLogging', 'value') === 'on';
        enableGenericTest = features.getFeatureProperty('edapFeatureHadoopGenericLogging', 'value') === 'on';
        enableNamedTest = features.getFeatureProperty('edapFeatureHadoopNamedLogging', 'value') === 'on';
        enableLoadedTest = features.getFeatureProperty('edapFeatureHadoopLoadedLogging', 'value') === 'on';
    }


    /**
     * The handler that is called for each event that is triggered.
     *
     * @param {String} [eventname] - Name of an EDAP event
     * @returns {Boolean} - true if this event should be logged to Hadoop,
     *                      false if it should not be logged to Hadoop
     */
    function shouldLogtoHadoop(eventName) {
        // Cache this so we don't have to process it twice in the condition below
        var isGeneric = genericRegex.test(eventName),
            isLoad = loadedRegex.test(eventName),
            isPageview = (eventName === 'pageview');

        // If it is a pageview and the pageview logging is enabled -OR-
        // If it is a generic event and generic logging is enabled -OR-
        // If it is a load event and load logging is enabled -OR-
        // If it is neither a pageview nor generic event nor load event and named event logging is enabled
        // then log the event to Hadoop
        if (
            (isPageview && enablePageviewTest) ||
            (isGeneric && enableGenericTest) ||
            (isLoad && enableLoadedTest) ||
            (!isPageview && !isGeneric && !isLoad && enableNamedTest)
        ) {
            return true;
        }

        return false;
    }


    /**
     * Function used to manipulate the ADL passed to a listener before sending to Hadoop
     *
     * @param {ADL} [adl] - ADL instance
     * @returns {ADL} - ADL instance with potential changes
     */
    function processADL(adl) {
        // Add a couple of special fields if they aren't already in the data object
        adl.setIfNotSet('pagehref', domHelpers.getHref());
        adl.setIfNotSet('edapintegrationsversion', domHelpers.getEdapIntegrationsVersion());
        adl.setIfNotSet('clienttimestamp', domHelpers.getMsFromEpoch());
        adl.setIfNotSet('parentpageviewid', pageIdHelper.getParentPageviewId(adl));

        return adl;
    }


    /**
     * The handler that is called for each event that is triggered.
     *
     * @param {Object} [edap] - Instance of edap or an EDAP Scope instance
     * @param {Object} [data] - Data object passed into every listener from EDAP
     * @param {Function} [setAsync] - Function to be called when the handler is async
     * @param {Function} [done] - Signals that the listener has completed. Can only be used when setAsync was executed previously
     */
    function handleEvent(edap, data, setAsync, done) {
        var adl = new ADL(data),
            eventName = adl.get('edapeventname');

        setAsync();

        // Bail if this is an ignored Hadoop Event
        if (isEventToIgnore(eventName)) {
            done();
            return;
        }

        // on pageviews, we should store the pageview ID so it's available as the previous pageview ID on the next page
        if (eventName === 'pageview') {
            pageIdHelper.storeCurrentPageviewId(adl.get('currentpageviewid'), adl);
        }

        // Check the Feature Flags and see if we should log here
        if (shouldLogtoHadoop(eventName)) {
            adl = processADL(adl);

            // Some events require special handling and so we should route them accordingly here
            // NOTE: If we start adding more special case handlers then we should move then into an object
            //       and use it as a lookup table to route.
            switch (eventName) {
                case 'user.email.entered':
                    userEventHandlers.processUserEmailEntered(hadoop, edap, adl).then(done);
                    break;

                default:
                    hadoop.send(eventName, adl.get(), adl.isSetGet('sessionid')).then(done);
            }
        } else {
            done();
        }
    }


    /**
     * Adds the Hadoop event listener
     *
     * @param {Object} [edap] - Instance of edap or an EDAP Scope instance
     */
    function doListenerSetup(edap) {
        // List of ignored events that we will not send to Hadoop regardless of the status of the feature flags
        setupEventsToIgnore(edap['public'].coreSupportedEvents);

        edap.on('*', function(data, setAsync, done) {
            handleEvent(edap, data, setAsync, done);
        });
    }


    /**
     * Function to setup listeners for this module
     *
     * @param {Object} [edap] - Instance of edap or an EDAP Scope instance
     */
    function setupListeners(edap) {
        /**
         * We do not want to log anything to Hadoop until we have recieved our FeatureFlag list from api-operation-edap Bizop.
         * We we register an onLoad callback that will be executed when edap['public'].features.load() is called.
         */
        edap['public'].features.onLoad(function() {
            // Process the Feature Flags and set the module globals accordingly
            readAndSetFeatureFlags(edap['public'].features);

            // If any of the tests are enabled then we need to add the * listener
            if (enableLoadedTest || enableNamedTest || enableGenericTest || enablePageviewTest) {
                doListenerSetup(edap);
            }
        });
    }

    return {
        setupListeners: setupListeners
    };
});

